import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { onStoreEvent } from '@/presentation/stores/utils/storeEventBus.ts'
import DependencyContainer from '@/presentation/configuration/DependencyContainer.ts'
import { createStoreBase } from '@/presentation/stores/resources/storeBase.ts'
import { useToast } from 'primevue/usetoast'
import { Assignment } from '@/core/domain/school/assignment/Assignment'
import { ValidModuleForAssignment } from '@/core/domain/school/assignment/ValidModuleForAssignment'
import type { AssignmentInputEntity } from '@/core/infrastructure/schemas/school/assignment/AssignmentSchema'
import { FetchAllAssignmentsUseCase } from '@/core/useCases/school/assignment/FetchAllAssignmentsUseCase'
import { FetchValidModulesForAssignmentUseCase } from '@/core/useCases/school/assignment/FetchValidModulesForAssignmentUseCase'
import { CreateAssignmentUseCase } from '@/core/useCases/school/assignment/CreateAssignmentUseCase'
import { UpdateAssignmentUseCase } from '@/core/useCases/school/assignment/UpdateAssignmentUseCase'
import { ClassroomDegree } from '@/core/domain/resources/enums/ClassroomDegree'

export const useAssignmentStore = defineStore('Assignment', () => {
  // Base commune
  const storeBase = createStoreBase([
    'fetchAssignments',
    'fetchValidModules',
    'createAssignment',
    'updateAssignment',
    'setModuleVisibility',
    'fetchClassroomAssignments'
  ])

  // Toasts
  const toast = useToast()

  // État
  const assignments = ref<Assignment[]>([])
  const validModules = ref<ValidModuleForAssignment[]>([])
  const currentSchoolId = ref<number | null>(null)
  const currentPeriodId = ref<number | null>(null)

  // Computed
  const assignmentsByDegree = computed(() => {
    const grouped = new Map<ClassroomDegree.Values, Assignment>()

    assignments.value.forEach((assignment) => {
      grouped.set(assignment.degree, assignment)
    })

    return grouped
  })

  const validModulesByOrder = computed(() => {
    return [...validModules.value].sort(ValidModuleForAssignment.compareByOrder)
  })

  const validModulesGroupedByCourse = computed(() => {
    return ValidModuleForAssignment.groupByCourse(validModules.value)
  })

  // Méthodes

  /**
   * Récupère tous les assignments pour une période scolaire
   */
  async function fetchAllAssignments(
    schoolId: number,
    periodId: number,
    forceRefresh = false
  ): Promise<Assignment[]> {
    // Éviter le rechargement inutile si les données sont déjà là et récentes pour la même école/période
    if (
      !forceRefresh &&
      assignments.value.length > 0 &&
      currentSchoolId.value === schoolId &&
      currentPeriodId.value === periodId &&
      storeBase.lastUpdated.value &&
      Date.now() - storeBase.lastUpdated.value.getTime() < 5 * 60 * 1000 // 5 minutes
    ) {
      return assignments.value
    }

    try {
      storeBase.loading.value.fetchAssignments = true
      storeBase.resetError()

      const response = await DependencyContainer.resolve(FetchAllAssignmentsUseCase).execute(
        schoolId,
        periodId
      )

      assignments.value = response
      currentSchoolId.value = schoolId
      currentPeriodId.value = periodId
      storeBase.updateLastUpdated()

      return response
    } catch (err) {
      storeBase.setError(err)
      return Promise.reject(err)
    } finally {
      storeBase.loading.value.fetchAssignments = false
    }
  }

  /**
   * Récupère les modules valides pour la matrice
   */
  async function fetchValidModulesForAssignment(
    schoolId: number,
    periodId: number,
    forceRefresh = false
  ): Promise<ValidModuleForAssignment[]> {
    // Éviter le rechargement inutile si les données sont déjà là et récentes pour la même école/période
    if (
      !forceRefresh &&
      validModules.value.length > 0 &&
      currentSchoolId.value === schoolId &&
      currentPeriodId.value === periodId &&
      storeBase.lastUpdated.value &&
      Date.now() - storeBase.lastUpdated.value.getTime() < 5 * 60 * 1000 // 5 minutes
    ) {
      return validModules.value
    }

    try {
      storeBase.loading.value.fetchValidModules = true
      storeBase.resetError()

      const response = await DependencyContainer.resolve(
        FetchValidModulesForAssignmentUseCase
      ).execute(schoolId, periodId)

      validModules.value = response
      storeBase.updateLastUpdated()

      return response
    } catch (err) {
      storeBase.setError(err)
      return Promise.reject(err)
    } finally {
      storeBase.loading.value.fetchValidModules = false
    }
  }

  /**
   * Crée un nouvel assignment
   */
  async function createAssignment(
    schoolId: number,
    periodId: number,
    assignmentData: AssignmentInputEntity
  ): Promise<Assignment> {
    try {
      storeBase.loading.value.createAssignment = true
      storeBase.resetError()

      const response = await DependencyContainer.resolve(CreateAssignmentUseCase).execute(
        schoolId,
        periodId,
        assignmentData
      )

      assignments.value.push(response)
      storeBase.updateLastUpdated()

      toast.add({
        severity: 'success',
        summary: 'Succès',
        detail: 'La modularisation a été créée avec succès',
        life: 3000
      })

      return response
    } catch (err) {
      storeBase.setError(err)
      toast.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Erreur lors de la création de la modularisation',
        life: 5000
      })
      return Promise.reject(err)
    } finally {
      storeBase.loading.value.createAssignment = false
    }
  }

  /**
   * Met à jour un assignment existant
   */
  async function updateAssignment(
    schoolId: number,
    periodId: number,
    assignmentId: number,
    assignmentData: AssignmentInputEntity
  ): Promise<Assignment> {
    try {
      storeBase.loading.value.updateAssignment = true
      storeBase.resetError()

      const response = await DependencyContainer.resolve(UpdateAssignmentUseCase).execute(
        schoolId,
        periodId,
        assignmentId,
        assignmentData
      )

      // Mettre à jour dans la liste
      const index = assignments.value.findIndex((a) => a.id === assignmentId)
      if (index !== -1) {
        assignments.value[index] = response
      }

      storeBase.updateLastUpdated()

      toast.add({
        severity: 'success',
        summary: 'Succès',
        detail: 'La modularisation a été mise à jour avec succès',
        life: 3000
      })

      return response
    } catch (err) {
      storeBase.setError(err)
      toast.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Erreur lors de la mise à jour de la modularisation',
        life: 5000
      })
      return Promise.reject(err)
    } finally {
      storeBase.loading.value.updateAssignment = false
    }
  }


  /**
   * Obtient un assignment par degré
   */
  function getAssignmentByDegree(degree: ClassroomDegree.Values): Assignment | undefined {
    return assignments.value.find((a) => a.degree === degree)
  }

  /**
   * Vérifie si un module est sélectionné pour un degré donné
   */
  function isModuleSelectedForDegree(moduleId: number, degree: ClassroomDegree.Values): boolean {
    const assignment = getAssignmentByDegree(degree)
    return assignment ? assignment.hasLearningModule(moduleId) : false
  }

  /**
   * Charge les données initiales pour une école/période
   */
  async function loadInitialData(
    schoolId: number,
    periodId: number,
    forceRefresh = false
  ): Promise<void> {
    try {
      await Promise.all([
        fetchValidModulesForAssignment(schoolId, periodId, forceRefresh),
        fetchAllAssignments(schoolId, periodId, forceRefresh)
      ])
    } catch (err) {
      console.error('Erreur lors du chargement des données initiales:', err)
      throw err
    }
  }

  /**
   * Réinitialise le store
   */
  function resetStore() {
    assignments.value = []
    validModules.value = []
    currentSchoolId.value = null
    currentPeriodId.value = null
    storeBase.resetError()
  }

  // Écouter l'événement de changement d'établissement pour se réinitialiser
  onStoreEvent('school:changed', () => {
    resetStore()
  })

  return {
    // État
    assignments: computed(() => assignments.value),
    validModules: computed(() => validModules.value),
    currentSchoolId: computed(() => currentSchoolId.value),
    currentPeriodId: computed(() => currentPeriodId.value),

    // Computed
    assignmentsByDegree,
    validModulesByOrder,
    validModulesGroupedByCourse,

    // Méthodes
    fetchAllAssignments,
    fetchValidModulesForAssignment,
    createAssignment,
    updateAssignment,
    getAssignmentByDegree,
    isModuleSelectedForDegree,
    loadInitialData,
    resetStore,

    // Base
    ...storeBase
  }
})
